import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { useNavigate, useParams } from 'react-router-dom';
import DynamicBreadcrumbs from '../../../components/Shared/Breadcrumb/index.tsx';
import Pagination from '../../../components/FlowBuilder/Common/Pagination/index.tsx';
import ModalForms from '../../../components/Shared/Modals/index.js';
import CommentBox from '../../../components/Shared/CommentBox/index.tsx';
import { CallingModal } from '../../../components/Modals/CallingModal/CallingModal.jsx';
import SearchableDropdown from '../../../components/FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import { ConstantActions } from '../../../Services/_actions/index.js';
import { dateFormate, indianTimeZone } from '../../../utils/helper.jsx';
import { FeatureThemeProvider } from '../../../theme/FeatureThemeProvider.jsx';
import useTemplateUsers from '../hooks/useTemplateUsers';
import useTemplateSx from '../hooks/useTemplateSx';
import TemplateDataTable from '../components/shared/TemplateDataTable';
import TemplateUsersFilters from '../components/users/TemplateUsersFilters';
import { TEMPLATE_BASE_PATH } from '../constants/templateApi';
import { TEMPLATE_PERMISSIONS } from '../constants/templatePermissions';
import { getTemplateButtonColumnDefs } from '../utils/templateButtonColumns';
import { formatTemplateRecipientContact } from '../utils/templateConstants';

const STATUS_OPTIONS = [
  { label: 'New Message Sent', value: 'new message sent' },
  { label: 'Delivered', value: 'delivered' },
  { label: 'Read', value: 'read' },
  { label: 'Failed', value: 'failed' },
];

const HIDDEN_API_FIELDS = new Set([
  'call_status',
  'last_call_status',
  'id',
  'partner',
  'company_id',
  'provider',
  'comments',
  'button_count_1',
  'button_count_2',
  'button_count_3',
  'button2',
  'button3',
  'button_2',
  'button_3',
]);

export default function TemplateUsersPage() {
  return (
    <FeatureThemeProvider>
      <TemplateUsersPageContent />
    </FeatureThemeProvider>
  );
}

function TemplateUsersPageContent() {
  const styles = useTemplateSx();
  const navigate = useNavigate();
  const constActions = ConstantActions();
  const { templateName } = useParams();

  const [sorting, setSorting] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);

  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState({});

  const {
    filters,
    users,
    totalCount,
    template,
    isLoading,
    callStatusList,
    actionRow,
    setActionRow,
    modalShowCall,
    setModalShowCall,
    onSearch,
    updateFilters,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSortingChange,
    handleSelectStatus,
    onSubmitComment,
    clearFilters,
  } = useTemplateUsers();

  const canAccessB2b = constActions.userHasPermission([
    TEMPLATE_PERMISSIONS.b2bFormPageAccess,
  ]);

  const breadcrumbsData = useMemo(
    () => [
      {
        label: (
          <IconButton color="primary">
            <HomeIcon />
          </IconButton>
        ),
        href: '/',
      },
      {
        label: 'Template List',
        href: `${TEMPLATE_BASE_PATH}/template-list`,
      },
      {
        label: 'Template Details',
        onClick: () => navigate(-1),
      },
      {
        label: 'User Template',
      },
    ],
    [navigate]
  );

  const handleActionClick = (action, row) => {
    setAnchorEl(null);
    setActionRow(row);

    if (action === 'Call & Comments') {
      setModalShowCall(true);
    } else if (action === 'B2B Form') {
      navigate(`/r/ops-verify/${row.phone_number}`);
    } else if (action === 'B2b2c Form') {
      navigate(`/r/lead-web-form/${row.phone_number}`);
    }
  };

  const buttonColumnDefs = useMemo(
    () => getTemplateButtonColumnDefs(template),
    [template]
  );

  const formatColumnHeader = (key) =>
    key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const formatCellValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return '—';
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return '—';
      }

      return value
        .map((item) => {
          if (typeof item === 'object' && item !== null) {
            return JSON.stringify(item);
          }

          return String(item);
        })
        .join(', ');
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    return String(value);
  };

  const formatFailureReason = (value) => {
    if (!value) {
      return '—';
    }

    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (item?.message) {
            return item.message;
          }

          if (item?.title) {
            return item.title;
          }

          return typeof item === 'object'
            ? JSON.stringify(item)
            : String(item);
        })
        .join(', ');
    }

    if (typeof value === 'object') {
      return (
        value?.message ||
        value?.title ||
        JSON.stringify(value)
      );
    }

    return String(value);
  };

  const columns = useMemo(
    () => {
      const baseColumns = [
        {
          accessorKey: 'index',
          header: 'S.No.',
          enableSorting: false,
          enableHiding: false,
          Cell: ({ row }) =>
            filters.page * filters.limit + row.index + 1,
        },

        {
          id: 'contact',
          header: 'Contact',
          enableSorting: false,
          accessorFn: (row) => formatTemplateRecipientContact(row),
          size: 220,
          Cell: ({ row }) => (
            <Typography
              variant="body2"
              noWrap
              sx={{ maxWidth: 220 }}
            >
              {formatTemplateRecipientContact(row.original)}
            </Typography>
          ),
        },

        {
          accessorKey: 'send_by',
          header: 'Send By',
          size: 160,
          Cell: ({ cell }) => (
            <Typography
              variant="body2"
              noWrap
              sx={{ maxWidth: 160 }}
              title={formatCellValue(cell.getValue())}
            >
              {formatCellValue(cell.getValue())}
            </Typography>
          ),
        },

        ...buttonColumnDefs
          .filter(
            ({ accessorKey }) =>
              !HIDDEN_API_FIELDS.has(accessorKey)
          )
          .map(({ accessorKey, label }) => ({
            accessorKey,
            header: label,
            size: Math.min(
              Math.max(label.length * 8, 80),
              160
            ),
          })),

        {
          accessorKey: 'status',
          header: 'Status',
        },

        {
          accessorKey: 'updated_at',
          header: 'Updated At',
          Cell: ({ cell }) =>
            cell.getValue()
              ? dateFormate(
                  indianTimeZone(cell.getValue()),
                  'DD-MM-YYYY hh:mm A'
                )
              : '—',
        },

        // {
        //   accessorKey: 'last_call_status',
        //   header: 'Call Status',
        //   enableSorting: false,
        //   Cell: ({ cell, row }) => (
        //     <Box sx={{ minWidth: 180 }}>
        //       <SearchableDropdown
        //         options={callStatusList}
        //         getOptionLabel={(option) =>
        //           option?.alais_name || ''
        //         }
        //         value={
        //           cell.getValue()
        //             ? {
        //                 alais_name: cell.getValue(),
        //               }
        //             : null
        //         }
        //         onChange={(newValue) =>
        //           handleSelectStatus(
        //             newValue,
        //             row.index
        //           )
        //         }
        //       />
        //     </Box>
        //   ),
        // },

        // {
        //   accessorKey: 'assigned_to',
        //   header: 'Assigned To',
        // },

        // {
        //   accessorKey: 'comments',
        //   header: 'Comments',
        //   enableSorting: false,
        //   Cell: ({ row }) => {
        //     const lastComment =
        //       row.original.comments?.at(-1)?.comment;

        //     return (
        //       <Typography
        //         variant="body2"
        //         noWrap
        //         sx={{ maxWidth: 180 }}
        //       >
        //         {lastComment || 'No comments'}
        //       </Typography>
        //     );
        //   },
        // },

        // {
        //   id: 'actions',
        //   accessorKey: 'actions',
        //   header: 'Action',
        //   enableSorting: false,
        //   size: 72,
        //   Cell: ({ row }) => (
        //     <IconButton
        //       size="small"
        //       onClick={(event) => {
        //         setAnchorEl(event.currentTarget);
        //         setActionRow(row.original);
        //       }}
        //     >
        //       <MoreVertIcon fontSize="small" />
        //     </IconButton>
        //   ),
        // },
      ];

      // Get all fields available in the API response
      const apiFields = new Set();

      users.forEach((user) => {
        Object.keys(user || {}).forEach((key) => {
          apiFields.add(key);
        });
      });

      // Get already existing column keys
      const existingColumnKeys = new Set(
        baseColumns
          .map((column) => column.accessorKey || column.id)
          .filter(Boolean)
      );

      // Create columns for API fields which are not already present
      // and are not included in the hidden fields list
      const dynamicColumns = Array.from(apiFields)
        .filter(
          (key) =>
            !existingColumnKeys.has(key) &&
            !HIDDEN_API_FIELDS.has(key)
        )
        .map((key) => ({
          accessorKey: key,
          header: formatColumnHeader(key),
          size: 160,

          Cell: ({ cell }) => {
            const value = cell.getValue();

            if (
              key === 'sent_at' ||
              key === 'delivered_at' ||
              key === 'read_at' ||
              key === 'failed_at' ||
              key === 'updated_at'
            ) {
              return value
                ? dateFormate(
                    indianTimeZone(value),
                    'DD-MM-YYYY hh:mm A'
                  )
                : '—';
            }

            if (key === 'failure_reason') {
              const reason = formatFailureReason(value);

              return (
                <Typography
                  variant="body2"
                  sx={{
                    maxWidth: 400,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  }}
                  title={reason}
                >
                  {reason}
                </Typography>
              );
            }

            if (key === 'failure_code') {
              return (
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ maxWidth: 180 }}
                >
                  {formatCellValue(value)}
                </Typography>
              );
            }

            return (
              <Typography
                variant="body2"
                noWrap
                sx={{
                  maxWidth: 300,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={formatCellValue(value)}
              >
                {formatCellValue(value)}
              </Typography>
            );
          },
        }));

      return [...baseColumns, ...dynamicColumns];
    },
    [
      users,
      buttonColumnDefs,
      filters.page,
      filters.limit,
      callStatusList,
      handleSelectStatus,
      setActionRow,
    ]
  );

  const selectableColumns = useMemo(
    () =>
      columns.filter(
        (column) =>
          column.accessorKey !== 'index' &&
          column.id !== 'index'
      ),
    [columns]
  );

  const isColumnVisible = (column) => {
    const columnKey =
      column.accessorKey || column.id;

    if (columnKey === 'index') {
      return true;
    }

    if (
      Object.prototype.hasOwnProperty.call(
        visibleColumns,
        columnKey
      )
    ) {
      return visibleColumns[columnKey];
    }

    return true;
  };

  const visibleTableColumns = useMemo(
    () =>
      columns.filter((column) =>
        isColumnVisible(column)
      ),
    [columns, visibleColumns]
  );

  const handleColumnVisibilityChange = (
    columnKey
  ) => {
    setVisibleColumns((previous) => ({
      ...previous,
      [columnKey]: !isColumnVisible(
        columns.find(
          (column) =>
            (column.accessorKey || column.id) ===
            columnKey
        )
      ),
    }));
  };

  const handleShowAllColumns = () => {
    const nextVisibility = {};

    selectableColumns.forEach((column) => {
      const columnKey =
        column.accessorKey || column.id;

      nextVisibility[columnKey] = true;
    });

    setVisibleColumns(nextVisibility);
  };

  const handleHideAllColumns = () => {
    const nextVisibility = {};

    selectableColumns.forEach((column) => {
      const columnKey =
        column.accessorKey || column.id;

      nextVisibility[columnKey] = false;
    });

    setVisibleColumns(nextVisibility);
  };

  return (
    <Box sx={styles.pageRoot}>
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={(_event, item) => {
          if (item?.href) {
            navigate(item.href);
          } else if (item?.onClick) {
            item.onClick();
          }
        }}
      />

      <Typography
        variant="h5"
        sx={{ ...styles.pageTitle, mb: 2 }}
      >
        Users for {templateName}
      </Typography>

      <Box sx={styles.dashboardSurface}>
        <TemplateUsersFilters
          search={filters.search}
          onSearchChange={onSearch}
          statusFilter={statusFilter}
          statusOptions={STATUS_OPTIONS}
          hasActiveFilters={Boolean(
            filters.search || filters.status
          )}
          onStatusChange={(option) => {
            setStatusFilter(option);

            updateFilters({
              status: option?.value ?? '',
              page: 0,
            });
          }}
          onClear={() => {
            setStatusFilter(null);
            clearFilters();
          }}
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            mb: 1,
            px: 1,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<ViewColumnIcon />}
            onClick={(event) =>
              setColumnMenuAnchor(
                event.currentTarget
              )
            }
          >
            Select Columns
          </Button>
        </Box>

        <Menu
          anchorEl={columnMenuAnchor}
          open={Boolean(columnMenuAnchor)}
          onClose={() =>
            setColumnMenuAnchor(null)
          }
          MenuListProps={{
            sx: {
              py: 1,
              minWidth: 280,
              maxHeight: 500,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              px: 2,
              pb: 1,
              gap: 1,
            }}
          >
            <Button
              size="small"
              onClick={handleShowAllColumns}
            >
              Show All
            </Button>

            <Button
              size="small"
              onClick={handleHideAllColumns}
            >
              Hide All
            </Button>
          </Box>

          {selectableColumns.map((column) => {
            const columnKey =
              column.accessorKey || column.id;

            return (
              <MenuItem
                key={columnKey}
                dense
                onClick={(event) => {
                  event.stopPropagation();

                  handleColumnVisibilityChange(
                    columnKey
                  );
                }}
                sx={{
                  py: 0,
                }}
              >
                <FormControlLabel
                  sx={{
                    width: '100%',
                    mr: 0,
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={isColumnVisible(
                        column
                      )}
                      onChange={() =>
                        handleColumnVisibilityChange(
                          columnKey
                        )
                      }
                    />
                  }
                  label={
                    typeof column.header === 'string'
                      ? column.header
                      : columnKey
                  }
                />
              </MenuItem>
            );
          })}
        </Menu>

        <TemplateDataTable
          columns={visibleTableColumns}
          data={users}
          state={{
            isLoading,
            sorting,
          }}
          onSortingChange={(updater) => {
            const next =
              typeof updater === 'function'
                ? updater(sorting)
                : updater;

            setSorting(next);
            handleSortingChange(next);
          }}
          enableDensityToggle={false}
          enableFullScreenToggle={false}
          enableColumnActions={false}
          enableColumnFilters={false}
          enableHiding={false}
          enableStickyHeader
          enableColumnPinning
          manualSorting
          enablePagination={false}
          initialState={{
            columnPinning: {
              right: ['actions'],
            },
          }}
          muiTableContainerProps={{
            sx: {
              maxHeight:
                'calc(100vh - 320px)',
            },
          }}
          renderBottomToolbar={() => (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                px: { xs: 1, sm: 1.5 },
                py: 1,
                borderTop: '1px solid',
                borderColor: 'grey.100',
              }}
            >
              <Pagination
                totalCount={totalCount}
                rowsPerPage={filters.limit}
                page={filters.page}
                handleChangePage={
                  handleChangePage
                }
                handleChangeRowsPerPage={
                  handleChangeRowsPerPage
                }
              />
            </Box>
          )}
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() =>
            handleActionClick(
              'Call & Comments',
              actionRow
            )
          }
        >
          Call &amp; Comments
        </MenuItem>

        {canAccessB2b ? (
          <>
            <MenuItem
              onClick={() =>
                handleActionClick(
                  'B2B Form',
                  actionRow
                )
              }
            >
              B2B Form
            </MenuItem>

            <MenuItem
              onClick={() =>
                handleActionClick(
                  'B2b2c Form',
                  actionRow
                )
              }
            >
              B2b2c Form
            </MenuItem>
          </>
        ) : null}
      </Menu>

      <ModalForms
        show={modalShowCall}
        handleClose={() =>
          setModalShowCall(false)
        }
        modalHeader="Call & Comments"
        modalBody={
          <Box>
            <CallingModal
              userDetails={actionRow}
            />

            <CommentBox
              commentList={
                actionRow?.comments ?? []
              }
              onSubmitComment={
                onSubmitComment
              }
            />
          </Box>
        }
      />
    </Box>
  );
}